import { useCallback, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'

import { isChildActive, isItemActive, splitDestination } from './navActive'
import {
  NAVIGATION,
  PLATFORM_NAVIGATION,
  RESELLER_NAVIGATION,
  type NavChild,
  type NavItem,
  type NavSection,
} from './navigation'
import { useNavBadges, type NavBadges } from './useNavBadges'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'

import { Brand } from '@/components/primitives/Brand'
import { Icon, NAV_ICONS } from '@/components/primitives/Icon'
import type { InboxCounts } from '@/features/inbox/types'
import { useResellerMe } from '@/features/reseller/api'
import { rememberWorkspace, useActiveWorkspaceSlug } from '@/features/workspaces/active'
import { useMyWorkspaces } from '@/features/workspaces/queries'
import { useCan, useCanAny } from '@/lib/permissions/useCan'
import { cn } from '@/lib/utils/cn'

const EXPANDED_KEY = 'convo:sidebar-expanded'

function readExpanded(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY)
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

/**
 * The workspace navigation.
 *
 * Groups expand in place rather than opening a flyout, because the sub-items
 * are FILTERS the agent switches between all day — "unread", "breached SLA",
 * "invoices" — and a menu that closes on every click makes that miserable.
 *
 * Colour carries meaning here, it is not decoration: every item keeps the same
 * tone as the status it leads to, so "breached SLA" is the same red on the
 * sidebar, in the ticket list, and on the ticket itself. Tones resolve to
 * contrast-verified token pairs in workspace.css, in both themes.
 */
export function Sidebar({
  workspaceSlug,
  collapsed,
  hasCommerce = true,
  scope = 'workspace',
}: {
  workspaceSlug: string
  collapsed: boolean
  hasCommerce?: boolean
  scope?: 'workspace' | 'platform' | 'reseller'
}) {
  // Platform and reseller panels are not workspace-scoped; they share the
  // absolute paths and the "back to workspaces" escape.
  const platform = scope !== 'workspace'
  // The reseller menu is a plain always-visible list (ResellerMenu), not an
  // expandable group: a remembered 'collapsed' choice could hide it for good.
  // Only the icon-only rail still uses the grouped form.
  const flatReseller = scope === 'reseller' && !collapsed
  const sections = flatReseller
    ? []
    : scope === 'platform'
      ? PLATFORM_NAVIGATION
      : scope === 'reseller'
        ? RESELLER_NAVIGATION
        : NAVIGATION.filter((s) => !s.requiresCommerce || hasCommerce)

  // Platform paths are absolute; workspace paths hang off the slug.
  const base = platform ? '' : `/w/${workspaceSlug}`

  const badges = useNavBadges()
  const isReseller = Boolean(useResellerMe().data)
  // On the reseller panel the workspace menu stays too, for the workspace the
  // reseller last had open — see useActiveWorkspaceSlug.
  const activeSlug = useActiveWorkspaceSlug()
  const { data: myWorkspaces, isSuccess: workspacesLoaded } = useMyWorkspaces()
  const noWorkspace = flatReseller && workspacesLoaded && (myWorkspaces?.length ?? 0) === 0
  const resellerWorkspaceSections =
    flatReseller && activeSlug
      ? NAVIGATION.filter((s) => !s.requiresCommerce || hasCommerce)
      : []
  const [overrides, setOverrides] = useState(readExpanded)
  const toggle = useCallback((path: string, next: boolean) => {
    setOverrides((prev) => {
      const updated = { ...prev, [path]: next }
      try {
        localStorage.setItem(EXPANDED_KEY, JSON.stringify(updated))
      } catch {
        /* Preference only. */
      }
      return updated
    })
  }, [])

  return (
    <nav
      aria-label={scope === 'platform' ? 'Platform' : scope === 'reseller' ? 'Reseller' : 'Main'}
      className={cn(
        'workspace-sidebar flex h-full flex-col border-r border-border bg-surface',
        platform && 'workspace-sidebar--platform',
        collapsed
          ? 'w-[var(--layout-sidebar-collapsed)]'
          : 'w-[var(--layout-sidebar)]',
      )}
    >
      <div className="sidebar-brand">
        <Brand compact={collapsed} />
      </div>
      <div className="p-3">
        {collapsed ? null : platform ? (
          <Link to="/workspaces" className="sidebar-escape">
            <Icon name="arrow" size={15} style={{ transform: 'rotate(180deg)' }} />
            <span>Back to workspaces</span>
          </Link>
        ) : (
          <WorkspaceSwitcher currentSlug={workspaceSlug} />
        )}
      </div>

      <div className="sidebar-scroll flex-1 overflow-y-auto px-3 pb-4">
        {/* A reseller keeps its own menu inside every workspace it opens —
            including a client company. It is a plain, always-visible list
            (no group to expand), so it can never push the workspace menu
            out of view or be left collapsed. Its paths are absolute. */}
        {((scope === 'workspace' && isReseller) || flatReseller) && !collapsed && (
          <ResellerMenu divider={scope === 'workspace'} />
        )}
        {sections.map((section, index) => (
          <Section
            key={section.label ?? index}
            section={section}
            base={base}
            collapsed={collapsed}
            isFirst={index === 0}
            badges={badges}
            overrides={overrides}
            onToggle={toggle}
            enforcePermissions={!platform}
          />
        ))}
        {noWorkspace && (
          <div className="mt-2 rounded-card border border-dashed border-border-strong p-4">
            <p className="text-compact font-semibold text-text">No workspace yet</p>
            <p className="mt-1 text-caption text-text-secondary">
              Create your own workspace to use Chatbots, Inbox, Leads and the rest. Your client
              companies will appear here too.
            </p>
            <Link
              to="/onboarding"
              className="pressable mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-control bg-action-solid px-4 text-compact font-semibold text-text-on-action hover:bg-action-solid-hover"
            >
              <Icon name="plus" size={15} />
              Create workspace
            </Link>
          </div>
        )}
        {resellerWorkspaceSections.length > 0 && (
          <div className="mt-1">
            {/* Which workspace the menu below describes — and a way to change it
                without leaving the reseller panel. */}
            <div className="mb-3 border-t border-border px-1 pt-4">
              <p className="mb-1.5 px-1 text-eyebrow font-semibold uppercase tracking-[0.06em] text-text-tertiary">
                Workspace
              </p>
              {activeSlug && (
                <WorkspaceSwitcher currentSlug={activeSlug} onSelect={rememberWorkspace} framed />
              )}
            </div>
            {resellerWorkspaceSections.map((section, index) => (
              <Section
                key={section.label ?? index}
                section={section}
                base={`/w/${activeSlug}`}
                collapsed={collapsed}
                isFirst={index === 0}
                hideLabel={index === 0}
                badges={badges}
                overrides={overrides}
                onToggle={toggle}
                enforcePermissions
              />
            ))}
          </div>
        )}
      </div>
      {!collapsed && (
        <div className="sidebar-footer">
          <Icon name="spark" size={16} />
          <span>Your conversations. Connected.</span>
        </div>
      )}
    </nav>
  )
}

function Section({
  section,
  base,
  collapsed,
  isFirst,
  badges,
  overrides,
  onToggle,
  enforcePermissions,
  hideLabel = false,
}: {
  section: NavSection
  base: string
  collapsed: boolean
  isFirst: boolean
  /** The caller already says which area this is (the workspace switcher). */
  hideLabel?: boolean
  badges: NavBadges
  overrides: Record<string, boolean>
  onToggle: (path: string, next: boolean) => void
  enforcePermissions: boolean
}) {
  const permitted = useCanAny(
    ...section.items.flatMap((item) => (item.permission ? [item.permission] : [])),
  )
  if (
    enforcePermissions &&
    !permitted &&
    section.items.every((item) => item.permission)
  ) {
    return null
  }

  return (
    <div className={cn('nav-section', !isFirst && 'mt-5')}>
      {section.label && !collapsed && !hideLabel && (
        <p
          className={cn(
            'nav-section-label mb-1 px-2 text-eyebrow font-semibold tracking-[0.06em] uppercase',
            section.tone && `nav-tone-${section.tone}`,
          )}
        >
          <span aria-hidden="true" className="nav-section-dot" />
          {section.label}
        </p>
      )}
      {!isFirst && collapsed && <div className="mx-2 my-3 border-t border-border" />}

      <ul>
        {section.items.map((item) => (
          <SidebarItem
            key={item.path}
            item={item}
            base={base}
            collapsed={collapsed}
            badges={badges}
            expandedOverride={overrides[item.path]}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </div>
  )
}

function SidebarItem({
  item,
  base,
  collapsed,
  badges,
  expandedOverride,
  onToggle,
}: {
  item: NavItem
  base: string
  collapsed: boolean
  badges: NavBadges
  expandedOverride: boolean | undefined
  onToggle: (path: string, next: boolean) => void
}) {
  const { pathname, search } = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])

  // A hidden item is a courtesy; the route guard and the API enforce it.
  const allowed = useCan(...(item.permission ? [item.permission] : []))
  const canQuickAction = useCan(
    ...(item.quickAction?.permission ? [item.quickAction.permission] : []),
  )

  const active = isItemActive(item, base, pathname)
  const children = item.children ?? []
  const quickAction = item.quickAction && canQuickAction ? item.quickAction : undefined
  const expandable = (children.length > 0 || Boolean(quickAction)) && !collapsed

  // Open when the route is inside the group, unless the user has said
  // otherwise — an explicit choice outlives navigating away.
  const expanded = expandedOverride ?? active
  const count = item.badge ? badges[item.badge] : undefined
  const groupId = `nav-group-${item.path.replace(/\W+/g, '-') || 'dashboard'}`

  if (item.permission && !allowed) return null

  return (
    <li className={cn('nav-entry', item.tone && `nav-tone-${item.tone}`)}>
      <div className="nav-row">
        <NavLink
          to={`${base}${item.path}`}
          end={item.path === ''}
          title={collapsed ? item.label : undefined}
          className={cn(
            'nav-item flex min-h-[44px] flex-1 items-center gap-3 rounded-control px-3 text-compact',
            'transition-colors duration-control ease-standard',
            active ? 'nav-item--active font-medium' : 'text-text-secondary',
            collapsed && 'justify-center px-0',
          )}
        >
          <span aria-hidden="true" className="nav-icon-chip">
            <Icon name={NAV_ICONS[item.path] ?? 'dashboard'} size={17} />
            {/* Collapsed to a rail there is no room for the number, but
                "something is waiting here" still has to survive. */}
            {collapsed && count !== undefined && count > 0 && (
              <span className="nav-chip-dot" />
            )}
          </span>
          {!collapsed && <span className="nav-label truncate">{item.label}</span>}
          {count !== undefined && count > 0 && (
            <span className={cn('nav-badge', collapsed && 'sr-only')}>
              {count > 99 ? '99+' : count}
            </span>
          )}
        </NavLink>

        {expandable && (
          <button
            type="button"
            className="nav-disclosure"
            aria-expanded={expanded}
            aria-controls={groupId}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.label}`}
            onClick={() => onToggle(item.path, !expanded)}
          >
            <Icon
              name="chevron"
              size={14}
              style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
            />
          </button>
        )}
      </div>

      {expandable && (
        <ul id={groupId} className="nav-children" hidden={!expanded}>
          {children.map((child) => (
            <SidebarChild
              key={child.to}
              child={child}
              siblings={children}
              base={base}
              pathname={pathname}
              params={params}
              counts={badges.inbox}
            />
          ))}
          {quickAction && (
            <li>
              <Link
                to={`${base}${item.path}?${quickAction.flag}=1`}
                className="nav-quick-action"
              >
                <Icon name="plus" size={15} />
                <span>{quickAction.label}</span>
              </Link>
            </li>
          )}
        </ul>
      )}
    </li>
  )
}

/** Counts the inbox endpoint already returns, matched to the sub-item. */
function childCount(to: string, counts: InboxCounts | undefined): number | undefined {
  if (!counts) return undefined
  const { path, params } = splitDestination(to)
  if (path !== '/inbox') return undefined
  const view = params.get('view') ?? 'all'
  return view in counts ? counts[view as keyof InboxCounts] : undefined
}

function SidebarChild({
  child,
  siblings,
  base,
  pathname,
  params,
  counts,
}: {
  child: NavChild
  siblings: readonly NavChild[]
  base: string
  pathname: string
  params: URLSearchParams
  counts: InboxCounts | undefined
}) {
  const allowed = useCan(...(child.permission ? [child.permission] : []))
  if (child.permission && !allowed) return null

  const active = isChildActive(child, siblings, base, pathname, params)
  const count = childCount(child.to, counts)

  return (
    <li>
      <Link
        to={`${base}${child.to}`}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'nav-child',
          child.tone && `nav-tone-${child.tone}`,
          active && 'nav-child--active',
        )}
      >
        <span aria-hidden="true" className="nav-child-dot" />
        <span className="truncate">{child.label}</span>
        {count !== undefined && count > 0 && (
          <span className="nav-child-count">{count > 99 ? '99+' : count}</span>
        )}
      </Link>
    </li>
  )
}

function ResellerMenu({ divider }: { divider: boolean }) {
  const { pathname, search } = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])
  const { data: me } = useResellerMe()
  const supportUnread = useNavBadges().supportUnread
  const children = RESELLER_NAVIGATION.flatMap((section) =>
    section.items.flatMap((item) => item.children ?? []),
  )
  // Real counts from the reseller profile — the things waiting on them.
  const counts: Record<string, number> = {
    '/reseller?tab=requests': me?.pending_requests ?? 0,
    '/reseller?tab=alerts': me?.unread_alerts ?? 0,
    '/reseller/support': supportUnread ?? 0,
  }

  return (
    <div className={cn('nav-section', divider && 'mb-4 border-b border-border pb-4')}>
      <p className="nav-section-label nav-tone-qualified mb-1 px-2 text-eyebrow font-semibold tracking-[0.06em] uppercase">
        <span aria-hidden="true" className="nav-section-dot" />
        Reseller panel
      </p>
      <ul className="space-y-0.5">
        {children.map((child) => {
          const active = isChildActive(child, children, '', pathname, params)
          const count = counts[child.to] ?? 0
          return (
            <li key={child.to}>
              <Link
                to={child.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'nav-child',
                  child.tone && `nav-tone-${child.tone}`,
                  active && 'nav-child--active',
                )}
              >
                <span aria-hidden="true" className="nav-child-dot" />
                <span className="truncate">{child.label}</span>
                {count > 0 && <span className="nav-child-count">{count > 99 ? '99+' : count}</span>}
              </Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
